import {NotificationType} from '@toolify/server/src/models/mongoose/NotificationModel/enum/NotificationType'
import {
  INotificationResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/INotificationResponseInstance'
import {INotificationItem} from '@toolify/client/src/modules/notifications/types/INotificationItem'
import {LogItemTitleItemType} from '@toolify/client/src/modules/activity/enum/LogItemTitleItemType'
import {
  NotificationResourceDetailsByConversationTypeMap,
} from '@toolify/client/src/modules/chat/maps/NotificationResourceDetailsByConversationTypeMap'
import {i18n} from '@toolify/client/src/setup/i18n'
import {
  findResourceByResourceTypeInPath,
  findResourceIdByResourceTypeInPath,
} from '@toolify/client/src/modules/activity/singletons/findResourceByResourceTypeInPath'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {
  NewMessageNotificationTitleStrategyByConversationTypeMap,
  defaultNewMessageNotificationTitleStrategy,
} from './NewMessageNotificationTitleStrategyByConversationTypeMap'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {NotificationWrapperType} from '../types/NotificationWapperType'
import {GetMessageNotificationWrapperTypeByConversationTypeMap} from './GetMessageNotificationWrapperTypeByConversationTypeMap'
import {GetMessageNotificationWrapperPayloadByConversationTypeMap} from './GetMessageNotificationWrapperPayloadByConversationTypeMap'
import {NotificationItemContentType} from '@toolify/client/src/modules/notifications/enum/NotificationItemContentType'
import {IWorkspaceResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IWorkspaceResponseInstance'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {PropertyTypeDataMap} from '@toolify/client/src/modules/databases/enum/DatabasePropertyType'
import {getPermissionTypeLabel} from '@toolify/client/src/modules/permissions/functions/getPermissionTypeLabel'
import {
  buildInlineNotificationResourceTitleItems,
} from '@toolify/client/src/modules/notifications/functions/buildInlineNotificationResourceTitleItems'
import {ResourceTypeIconMap} from '@toolify/client/src/modules/resources/maps/ResourceTypeIconMap'
import {PermissionEntityType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/PermissionEntityType'
import {
  IWorkspacePermissionRequestRequestedForResponseEntity,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IWorkspacePermissionRequestRequestedForResponseEntity'
import {LogTitleItem} from '@toolify/client/src/modules/activity/types/ILogTitleItem'
import {getRequestedForEntityPhrase} from '@toolify/client/src/modules/notifications/functions/getRequestedForEntityPhrase'

function getRequestedForIconNameType(entityType: PermissionEntityType): IconNameType | undefined {
  switch(entityType) {
  case PermissionEntityType.App:
    return IconNameType.Bolt
  case PermissionEntityType.Group:
    return IconNameType.UserFriends
  default:
    return undefined
  }
}

function buildRequestedForTitleItems(
  requestedFor: IWorkspacePermissionRequestRequestedForResponseEntity | null | undefined,
): LogTitleItem[] {
  if(!requestedFor) {
    return []
  }
  const labelItem: LogTitleItem = {
    type: LogItemTitleItemType.String,
    payload: getRequestedForEntityPhrase(requestedFor.entityType),
  }
  if(requestedFor.entityType === PermissionEntityType.User && requestedFor.payload?.entity) {
    return [
      labelItem,
      {
        type: LogItemTitleItemType.Entity,
        payload: requestedFor.payload.entity,
      },
    ]
  }
  return [
    labelItem,
    {
      type: LogItemTitleItemType.Item,
      payload: {
        text: requestedFor.name || requestedFor.entityId,
        iconNameType: getRequestedForIconNameType(requestedFor.entityType),
      },
    },
  ]
}

const {t} = i18n.global

export const NotificationTypeItemMap = new Map<NotificationType,(notification: INotificationResponseInstance<NotificationType>) => Partial<INotificationItem<NotificationType>>>([
  [NotificationType.DatabaseItemCreated, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.DatabaseItemCreated>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const database = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = notificationItem.payload?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName ? {
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: database?.id,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    } : null
    return {
      primaryIconClass: IconNameType.Plus,
      secondaryIconClass: 'bg-green',
      to,
      resourceName: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.createdNewItem'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: databaseItem ? `${databaseItem.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.itemRemoved'),
            iconNameType: findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)?.iconNameType,
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: database?.iconNameType,
        }),
      ],
      notificationWrapperType: NotificationWrapperType.DatabaseItem,
      notificationWrapperPayload: {
        databaseId: database ? database.id : null,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    }
  }],
  [NotificationType.UserAssignedToDatabaseItem, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.UserAssignedToDatabaseItem>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const database = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = notificationItem.payload?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName ? {
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: database?.id,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    } : null
    return {
      primaryIconClass: IconNameType.Plus,
      secondaryIconClass: 'bg-green',
      to: to,
      resourceName: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.assignedYouTo'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: databaseItem ? `${databaseItem.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.itemRemoved'),
            iconNameType: findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)?.iconNameType,
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: database?.iconNameType,
        }),
      ],
      notificationWrapperType: NotificationWrapperType.DatabaseItem,
      notificationWrapperPayload: {
        databaseId: notificationItem.payload?.databaseId,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
        workspaceName: notificationItem.payload?.workspaceName,
      },
    }
  }],
  [NotificationType.UserAssigneeChangedOnDatabaseItem, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.UserAssigneeChangedOnDatabaseItem>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const database = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = notificationItem.payload?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName ? {
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: database?.id,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    } : null
    const databaseResourceName = database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved')
    return {
      primaryIconClass: IconNameType.Rotate,
      secondaryIconClass: '',
      to,
      resourceName: databaseResourceName,
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.changed'),
        },
        {
          type: LogItemTitleItemType.String,
          payload: (
            PropertyTypeDataMap.get(notificationItem.payload.databaseProperty.type)?.titleKey
              ? t(PropertyTypeDataMap.get(notificationItem.payload.databaseProperty.type).titleKey)
              : notificationItem.payload.databaseProperty.title
          )?.toLowerCase(),
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.of'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: databaseItem ? `${databaseItem.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.itemRemoved'),
            iconNameType: findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)?.iconNameType,
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: database?.iconNameType,
        }),
      ],
      contentType: NotificationItemContentType.DatabaseItemValue,
      contentPayload: notificationItem.payload,
      notificationWrapperType: NotificationWrapperType.DatabaseItem,
      notificationWrapperPayload: {
        databaseId: database ? database.id : null,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    }

  }],
  [NotificationType.StatusChangedOnDatabaseItem, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.StatusChangedOnDatabaseItem>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const database = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = (notificationItem.payload as {workspaceName?: string})?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName ? {
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: database?.id,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    } : null
    return {
      primaryIconClass: IconNameType.Rotate,
      secondaryIconClass: '',
      to,
      resourceName: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.changed'),
        },
        {
          type: LogItemTitleItemType.String,
          payload: (
            PropertyTypeDataMap.get(notificationItem.payload.databaseProperty.type)?.titleKey
              ? t(PropertyTypeDataMap.get(notificationItem.payload.databaseProperty.type).titleKey)
              : notificationItem.payload.databaseProperty.title
          )?.toLowerCase(),
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.of'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: databaseItem ? `${databaseItem.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.itemRemoved'),
            iconNameType: findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)?.iconNameType,
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: database?.iconNameType,
        }),
      ],
      contentType: NotificationItemContentType.DatabaseItemValue,
      contentPayload: notificationItem.payload,
      notificationWrapperType: NotificationWrapperType.DatabaseItem,
      notificationWrapperPayload: {
        databaseId: database ? database.id : null,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    }

  }],
  [NotificationType.LabelChangedOnDatabaseItem, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.LabelChangedOnDatabaseItem>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const database = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = (notificationItem.payload as {workspaceName?: string})?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName ? {
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: database?.id,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    } : null
    return {
      primaryIconClass: IconNameType.Rotate,
      secondaryIconClass: '',
      to,
      resourceName: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.changed'),
        },
        {
          type: LogItemTitleItemType.String,
          payload: (
            PropertyTypeDataMap.get(notificationItem.payload.databaseProperty.type)?.titleKey
              ? t(PropertyTypeDataMap.get(notificationItem.payload.databaseProperty.type).titleKey)
              : notificationItem.payload.databaseProperty.title
          )?.toLowerCase(),
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.of'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: databaseItem ? `${databaseItem.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.itemRemoved'),
            iconNameType: findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)?.iconNameType,
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: database?.iconNameType,
        }),
      ],
      contentType: NotificationItemContentType.DatabaseItemValue,
      contentPayload: notificationItem.payload,
      notificationWrapperType: NotificationWrapperType.DatabaseItem,
      notificationWrapperPayload: {
        databaseId: database ? database.id : null,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    }

  }],
  [NotificationType.DatabaseItemDateChanged, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.DatabaseItemDateChanged>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const database = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = (notification.payload as { workspaceName?: string })?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName ? {
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: database?.id,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    } : null
    return {
      primaryIconClass: IconNameType.Rotate,
      secondaryIconClass: '',
      to,
      resourceName: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.changed'),
        },
        {
          type: LogItemTitleItemType.String,
          payload: (
            PropertyTypeDataMap.get(notificationItem.payload.databaseProperty.type)?.titleKey
              ? t(PropertyTypeDataMap.get(notificationItem.payload.databaseProperty.type).titleKey)
              : notificationItem.payload.databaseProperty.title
          )?.toLowerCase(),
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.of'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: databaseItem ? `${databaseItem.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.itemRemoved'),
            iconNameType: findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)?.iconNameType,
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: database?.iconNameType,
        }),
      ],
      contentType: NotificationItemContentType.DatabaseItemValue,
      contentPayload: notificationItem.payload,
      notificationWrapperType: NotificationWrapperType.DatabaseItem,
      notificationWrapperPayload: {
        databaseId: database ? database.id : null,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
        workspaceName: (notificationItem.resourcePath.find(resource => resource.resourceType === ResourceType.Workspace)?.resource as IWorkspaceResponseInstance)?.name,
      },
    }

  }],
  [NotificationType.MentionedInMessage, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.MentionedInMessage>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const notificationResourceDetailsFn = NotificationResourceDetailsByConversationTypeMap[notificationItem.payload.conversationType]
    if(!notificationResourceDetailsFn) {
      return null
    }
    const notificationResourceDetails = notificationResourceDetailsFn(
      notificationItem.payload.conversationNotificationPayload,
      notification.resourcePath,
      {
        authorEntity: notification.authorEntity,
      },
    )
    return {
      primaryIconClass: IconNameType.Comment,
      secondaryIconClass: 'bg-blue',
      to: notificationResourceDetails.route,
      resourceName: notificationResourceDetails.resourceName,
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.mentionedYou'),
        },
        ...notificationResourceDetails.titleItems,
      ],
      contentType: NotificationItemContentType.Message,
      contentPayload: notificationItem.payload,
      notificationWrapperType: GetMessageNotificationWrapperTypeByConversationTypeMap.get(notificationItem.payload.conversationType),
      notificationWrapperPayload: GetMessageNotificationWrapperPayloadByConversationTypeMap.get(notificationItem.payload.conversationType)?.(notificationItem.payload.conversationNotificationPayload),
    }
  }],
  [NotificationType.MentionedInDocument, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.MentionedInDocument>
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = notificationItem.payload?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const documentId = notificationItem.payload?.documentId ?? findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.Document)
    const documentTitle = notificationItem.payload?.documentTitle || t('general.noTitle')
    const to = workspaceName && documentId ? {
      name: RouteName.Document,
      params: {
        workspaceName,
        documentId,
      },
    } : null
    return {
      primaryIconClass: IconNameType.At,
      secondaryIconClass: 'bg-blue',
      to,
      resourceName: documentTitle,
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.mentionedYou'),
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.inThe'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: documentTitle,
            iconNameType: IconNameType.FileLines,
          },
        },
      ],
    }
  }],
  [NotificationType.MentionedInMarkdown, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.MentionedInMarkdown>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const database = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = notificationItem.payload?.workspaceName || (workspace?.resource as IWorkspaceResponseInstance)?.name || workspace?.resourceId
    const databaseItemId = findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const to = workspaceName ? {
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: database?.id,
        databaseItemId,
      },
    } : null
    return {
      primaryIconClass: IconNameType.At,
      secondaryIconClass: 'bg-blue',
      to,
      resourceName: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.mentionedYou'),
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.in'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: databaseItem ? `${databaseItem.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.itemRemoved'),
            iconNameType: database?.iconNameType,
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: database?.iconNameType,
        }),
      ],
      notificationWrapperType: NotificationWrapperType.DatabaseItem,
      notificationWrapperPayload: {
        databaseId: database ? database.id : null,
        databaseItemId,
        workspaceName,
      },
    }
  }],
  [NotificationType.NewMessage, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.NewMessage>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const notificationResourceDetailsFn = NotificationResourceDetailsByConversationTypeMap[notificationItem.payload.conversationType]
    if(!notificationResourceDetailsFn) {
      return null
    }
    const notificationResourceDetails = notificationResourceDetailsFn(
      notificationItem.payload.conversationNotificationPayload,
      notification.resourcePath,
      {
        omitInPrefix: true,
        authorEntity: notification.authorEntity,
      },
    )
    const newMessageTitleStrategy = NewMessageNotificationTitleStrategyByConversationTypeMap.get(notificationItem.payload.conversationType) ?? defaultNewMessageNotificationTitleStrategy
    // eventCount survives reading the group, so a read group still shows how many messages it holds
    const newMessageActionText = newMessageTitleStrategy.getActionText(notification.eventCount ?? notification.unreadGroupCount)
    return {
      primaryIconClass: IconNameType.Comment,
      secondaryIconClass: 'bg-grey',
      to: notificationResourceDetails.route,
      resourceName: notificationResourceDetails.resourceName,
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: newMessageActionText,
        },
        ...(newMessageTitleStrategy.includeResourceTitleItems ? notificationResourceDetails.titleItems : []),
      ],
      contentType: NotificationItemContentType.Message,
      contentPayload: notificationItem.payload,
      notificationWrapperType: GetMessageNotificationWrapperTypeByConversationTypeMap.get(notificationItem.payload.conversationType),
      notificationWrapperPayload: GetMessageNotificationWrapperPayloadByConversationTypeMap.get(notificationItem.payload.conversationType)?.(notificationItem.payload.conversationNotificationPayload),
      isAggregationGroup: (notification.eventCount ?? notification.unreadGroupCount) > 1,
    }
  }],
  [NotificationType.RepliedToMyMessage, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.RepliedToMyMessage>
    const notificationResourceDetailsFn = NotificationResourceDetailsByConversationTypeMap[notificationItem.payload.conversationType]
    if(!notificationResourceDetailsFn) {
      return null
    }
    const notificationResourceDetails = notificationResourceDetailsFn(
      notificationItem.payload.conversationNotificationPayload,
      notification.resourcePath,
      {
        authorEntity: notification.authorEntity,
      },
    )
    return {
      primaryIconClass: IconNameType.Comment,
      secondaryIconClass: 'bg-blue',
      to: notificationResourceDetails.route,
      resourceName: notificationResourceDetails.resourceName,
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.repliedToMyMessage'),
        },
        ...notificationResourceDetails.titleItems,
      ],
      contentType: NotificationItemContentType.Message,
      contentPayload: notificationItem.payload,
      notificationWrapperType: GetMessageNotificationWrapperTypeByConversationTypeMap.get(notificationItem.payload.conversationType),
      notificationWrapperPayload: GetMessageNotificationWrapperPayloadByConversationTypeMap.get(notificationItem.payload.conversationType)?.(notificationItem.payload.conversationNotificationPayload),
    }
  }],
  [NotificationType.DatabaseItemFileCreated, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.DatabaseItemFileCreated>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const database = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = notificationItem.payload?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName ? {
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: database?.id,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    } : null
    return {
      primaryIconClass: IconNameType.FileUpload,
      secondaryIconClass: 'bg-blue',
      to,
      resourceName: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.addedAttachment'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: databaseItem ? `${databaseItem.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.itemRemoved'),
            iconNameType: findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)?.iconNameType,
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: database?.iconNameType,
        }),
      ],
      notificationWrapperType: NotificationWrapperType.DatabaseItem,
      notificationWrapperPayload: {
        databaseId: database ? database.id : null,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    }
  }],
  [NotificationType.DatabaseItemFileDeleted, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.DatabaseItemFileDeleted>
    const databaseItem = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem)
    const database = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = notificationItem.payload?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName ? {
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: database?.id,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    } : null
    return {
      primaryIconClass: IconNameType.File,
      secondaryIconClass: 'bg-red',
      to,
      resourceName: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.removed'),
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.attachmentFrom'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: databaseItem ? `${databaseItem.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.itemRemoved'),
            iconNameType: findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)?.iconNameType,
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: database ? `${database.title || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: database?.iconNameType,
        }),
      ],
      notificationWrapperType: NotificationWrapperType.DatabaseItem,
      notificationWrapperPayload: {
        databaseId: database ? database.id : null,
        databaseItemId: findResourceIdByResourceTypeInPath(notification.resourcePath, ResourceType.DatabaseItem),
      },
    }
  }],
  [NotificationType.CloudItemCreated, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.CloudItemCreated>
    const folder = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Folder)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = notificationItem.payload?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName && folder ? {
      name: RouteName.Folder,
      params: {
        workspaceName,
        folderId: folder.id,
      },
    } : null
    return {
      primaryIconClass: IconNameType.CirclePlus,
      secondaryIconClass: 'bg-green',
      to,
      resourceName: folder ? `${folder.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.addedItemToCloud'),
        },
        ...buildInlineNotificationResourceTitleItems({
          text: folder ? `${folder.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: IconNameType.Folder,
        }),
      ],
    }
  }],
  [NotificationType.CloudItemDeleted, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.CloudItemDeleted>
    const folder = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Folder)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = notificationItem.payload?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName && folder ? {
      name: RouteName.Folder,
      params: {
        workspaceName,
        folderId: folder.id,
      },
    } : null
    return {
      primaryIconClass: IconNameType.Folder,
      secondaryIconClass: 'bg-red',
      to,
      resourceName: folder ? `${folder.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.removedItemFromCloud'),
        },
        ...buildInlineNotificationResourceTitleItems({
          text: folder ? `${folder.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: IconNameType.Folder,
        }),
      ],
    }
  }],
  [NotificationType.WorkspaceAppAccessRequestPending, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.WorkspaceAppAccessRequestPending>
    const p = notificationItem.payload
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = p?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? ''
    return {
      primaryIconClass: IconNameType.Bolt,
      secondaryIconClass: 'bg-blue',
      to: null,
      resourceName: workspaceName,
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.workspaceAppAccessRequestPending'),
        },
        ...buildInlineNotificationResourceTitleItems({
          text: workspaceName || t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: ResourceTypeIconMap.get(ResourceType.Workspace),
        }),
      ],
      workspaceAppAccessPayload: {
        requestId: p.requestId,
        appName: p.appName,
        workspaceName: p.workspaceName,
        requesterName: p.requesterName,
        status: p.status ?? null,
        reviewedByEntity: p.reviewedByEntity ?? null,
      },
      notificationWrapperType: NotificationWrapperType.WorkspaceAppAccessRequest,
      notificationWrapperPayload: {
        requestId: p.requestId,
        appName: p.appName,
        workspaceName: p.workspaceName,
        requesterName: p.requesterName,
        status: p.status ?? null,
        reviewedByEntity: p.reviewedByEntity ?? null,
      },
    }
  }],
  [NotificationType.WorkspacePermissionRequestPending, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.WorkspacePermissionRequestPending>
    const p = notificationItem.payload
    const request = p.workspacePermissionRequest
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = p.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? ''
    const viaEntity = notificationItem.viaEntity ?? p.viaEntity ?? null
    const targetResource = findResourceByResourceTypeInPath(notification.resourcePath, request.resourceType)
    const inlineResourceType = targetResource ? request.resourceType : ResourceType.Workspace
    const inlineResourceText = targetResource
      ? ((targetResource as {title?: string; name?: string}).title
        || (targetResource as {name?: string}).name
        || t('general.noTitle'))
      : (workspaceName || t('notifications.notificationItemTitleItems.resourceRemoved'))
    const requestedFor = request.requestedFor ?? null
    return {
      primaryIconClass: IconNameType.UserSecret,
      secondaryIconClass: 'bg-blue',
      to: null,
      resourceName: workspaceName,
      viaEntity,
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.workspacePermissionRequestPending'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: getPermissionTypeLabel(request.permissionType).toLocaleLowerCase(),
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: inlineResourceText,
          iconNameType: ResourceTypeIconMap.get(inlineResourceType),
        }),
        ...buildRequestedForTitleItems(requestedFor),
      ],
      workspacePermissionRequestPayload: {
        requestId: request.id,
        requestedFor,
        workspaceName: p.workspaceName,
        authorEntity: notification.authorEntity,
        viaEntity,
        permissionType: request.permissionType,
        status: request.status,
        reviewedByEntity: request.reviewedByEntity ?? null,
      },
      notificationWrapperType: NotificationWrapperType.WorkspacePermissionRequest,
      notificationWrapperPayload: {
        requestId: request.id,
        requestedFor,
        workspaceName: p.workspaceName,
        authorEntity: notification.authorEntity,
        viaEntity,
        permissionType: request.permissionType,
        status: request.status,
        reviewedByEntity: request.reviewedByEntity ?? null,
      },
    }
  }],
  [NotificationType.AppVersionPublished, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.AppVersionPublished>
    const automation = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Automation)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName && automation ? {
      name: RouteName.Automation,
      params: {
        workspaceName,
        automationId: automation.id,
      },
    } : null
    return {
      primaryIconClass: IconNameType.Bolt,
      secondaryIconClass: 'bg-blue',
      to,
      resourceName: notificationItem.payload.automationName,
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: 'published a new version of',
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: `${notificationItem.payload.appName} · ${notificationItem.payload.appItemName} (v${notificationItem.payload.version})`,
            iconNameType: IconNameType.Bolt,
          },
        },
        ...buildInlineNotificationResourceTitleItems({
          text: notificationItem.payload.automationName || t('notifications.notificationItemTitleItems.resourceRemoved'),
          iconNameType: IconNameType.Bolt,
        }),
      ],
    }
  }],
  [NotificationType.ExportedDatabase, (notification: INotificationResponseInstance) => {
    const notificationItem = notification as INotificationResponseInstance<NotificationType.ExportedDatabase>
    const database = findResourceByResourceTypeInPath(notification.resourcePath, ResourceType.Database)
    const workspace = notification.resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = notificationItem.payload?.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId
    const to = workspaceName && database ? {
      name: RouteName.Database,
      params: {
        workspaceName,
        databaseId: database.id,
      },
    } : null
    return {
      primaryIconClass: IconNameType.FileExport,
      secondaryIconClass: 'bg-blue',
      to,
      resourceName: notificationItem.payload.databaseName,
      titleItems: [
        {
          type: LogItemTitleItemType.Entity,
          payload: notification.authorEntity,
        },
        {
          type: LogItemTitleItemType.String,
          payload: t('notifications.notificationItemTitleItems.exportedDatabase'),
        },
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: notificationItem.payload.fileName,
            iconNameType: IconNameType.FileExport,
          },
        },
      ],
    }
  }],
])
